import {localizeConversationDisplay} from "@/lib/i18n/conversation-copy"
import { useInboxPresentation } from "@/hooks/use-inbox-presentation"
import { useConversationTimeFormatters } from "@/hooks/use-conversation-time-formatters"
import { Fragment, type ReactNode } from "react"
import { BotOff } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { ChannelIcon } from "@/components/ui/channel-icon"
import { conversationText } from "@/lib/i18n/conversation-copy"
import { cn } from "@/lib/utils"
import { contactInitials } from "@/lib/conversation/initials"

import { getPauseOriginCopy } from "@/lib/conversation/pause-origin-label"
import { type InboxSourceKind, type UnifiedInboxItem } from "@/lib/inbox/types"
import { useTranslations } from "next-intl"


/** One tint per source, so the kind of conversation is readable before the text is. */
const SOURCE_BADGE: Record<InboxSourceKind, string> = {
  message: "border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-800 dark:bg-sky-950/60 dark:text-sky-300",
  email: "border-violet-300 bg-violet-100 text-violet-800 dark:border-violet-800 dark:bg-violet-950/60 dark:text-violet-300",
  voice: "border-teal-300 bg-teal-100 text-teal-800 dark:border-teal-800 dark:bg-teal-950/60 dark:text-teal-300",
}

interface InboxRowProps {
  item: UnifiedInboxItem
  isActive: boolean
  onClick: () => void
  timezone: string
  /** What the surface already says in its header, so the row need not repeat it. */
  surfaceOwns?: ReadonlyArray<"source" | "agent">
}

export function InboxRow({ item: canonicalItem, isActive, onClick, timezone, surfaceOwns = [] }: InboxRowProps) {
  const { INBOX_SOURCE_LABELS } = useInboxPresentation()

  const { formatListTimestamp } = useConversationTimeFormatters()

  const tCopy = useTranslations("ConversationCopy")
  const tUi = useTranslations("Feedback.InboxRow")
  const item = localizeConversationDisplay(canonicalItem, tCopy)

  const contact = localizeConversationDisplay(item.contact, tCopy)
  const needsAttention = item.attention.length > 0
  const identity = identityLine(item)
  const showSource = !surfaceOwns.includes("source")
  const meta = metaLine(item, surfaceOwns, tUi)

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-start gap-3 border-b border-l-2 px-3 py-3 text-left transition-colors",
        isActive
          ? "border-l-blue-500 bg-blue-50 dark:bg-blue-950/30"
          : "border-l-transparent hover:bg-muted/50",
        item.unreadCount > 0 && !isActive && "bg-blue-50/40 dark:bg-blue-950/10",
        needsAttention && !isActive && "border-l-amber-500",
      )}
    >
      <div className="relative flex-shrink-0">
        <Avatar className="h-10 w-10">
          <AvatarImage src={item.contact.avatarUrl ?? undefined} />
          <AvatarFallback className="text-xs">{contactInitials(contact.title)}</AvatarFallback>
        </Avatar>
        <div className="absolute -bottom-0.5 -right-0.5">
          <ChannelIcon channelType={item.channel.kind} size={12} />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            <span
              className={cn("truncate text-sm", item.unreadCount > 0 ? "font-semibold" : "font-medium")}
            >
              {contact.title}
            </span>
            {item.contact.identityCaptured && (
              <Badge
                variant="outline"
                title={tUi("theAgentReadThisSenderOutOfThe")}
                className="h-4 flex-shrink-0 px-1 text-[9px] font-medium text-muted-foreground"
              >
                <span key={tUi("captured")}>{tUi("captured")}</span></Badge>
            )}
          </span>
          <div className="flex flex-shrink-0 items-center gap-1">
            {/* Named the way the agent's own list names it, so one conversation
                does not read as two different states in two places. */}
            {item.pausedBy && (
              <span
                title={tUi("aIPausedValue0", { value0: String(conversationText(getPauseOriginCopy(item.pausedBy), tCopy)) })}
                className="flex items-center gap-0.5 text-amber-500"
              >
                <BotOff className="h-3 w-3" />
                <span className="text-[10px] leading-none">{conversationText(getPauseOriginCopy(item.pausedBy), tCopy)}</span>
              </span>
            )}
            <span
              className={cn(
                "text-xs",
                item.unreadCount > 0 ? "font-medium text-blue-600" : "text-muted-foreground",
              )}
            >
              {formatListTimestamp(item.activityAt, timezone)}
            </span>
          </div>
        </div>

        {(showSource || meta.length > 0) && (
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            {showSource && (
              <Badge
                variant="outline"
                className={cn("h-5 px-1.5 text-[11px] font-semibold", SOURCE_BADGE[item.source])}
              >
                {INBOX_SOURCE_LABELS[item.source].one}
              </Badge>
            )}
            {meta.map((part, index) => (
              <Fragment key={index}>
                {index > 0 && <span aria-hidden>·</span>}
                {part}
              </Fragment>
            ))}
          </div>
        )}

        {identity && (
          <p
            className={cn(
              "truncate text-[11px] text-muted-foreground",
              identity.mono && "font-mono",
            )}
          >
            {identity.value}
          </p>
        )}

        <div className="mt-0.5 flex items-center justify-between gap-2">
          <p
            className={cn(
              "truncate text-xs",
              item.unreadCount > 0 ? "font-medium text-foreground" : "text-muted-foreground",
            )}
          >
            {previewText(tUi, item)}
          </p>
          <div className="flex flex-shrink-0 items-center gap-1">
            {needsAttention && (
              <Badge className="h-5 bg-amber-500 px-1.5 text-[10px] hover:bg-amber-500">
                <span key={tUi("needsAHuman")}>{tUi("needsAHuman")}</span></Badge>
            )}
            {item.unreadCount > 0 && (
              <Badge className="h-5 min-w-5 px-1.5 text-[10px]">{item.unreadCount}</Badge>
            )}
          </div>
        </div>

        {item.status && <p className="text-[10px] font-semibold text-foreground">{item.status}</p>}
      </div>
    </button>
  )
}

/**
 * Owning agent and account, `·`-separated, minus what the surface already states.
 * The agent is named or reported missing, never guessed.
 */
function metaLine(item: UnifiedInboxItem, surfaceOwns: ReadonlyArray<"source" | "agent">, tUi: ReturnType<typeof useTranslations<"Feedback.InboxRow">>): ReactNode[] {
  const parts: ReactNode[] = []
  if (!surfaceOwns.includes("agent")) {
    parts.push(<span className="truncate">{item.agent?.name ?? tUi("noAgent")}</span>)
  }
  if (item.channel.accountName) {
    parts.push(<span className="truncate font-mono">{item.channel.accountName}</span>)
  }
  return parts
}

/**
 * The strongest identifier the source actually stored — nothing is invented, and
 * the fallback line is NOT monospaced: `contact.subtitle` is an email subject,
 * which is context, not an identifier.
 */
function identityLine(item: UnifiedInboxItem): { value: string; mono: boolean } | null {
  const candidates = [item.contact.handle && `@${item.contact.handle}`, item.contact.email, item.contact.phone]
  const identifier = candidates.find(
    (value): value is string => !!value && value.toLowerCase() !== item.contact.title.toLowerCase(),
  )
  if (identifier) return { value: identifier, mono: true }
  return item.contact.subtitle ? { value: item.contact.subtitle, mono: false } : null
}

function previewText(tUi: ReturnType<typeof useTranslations<"Feedback.InboxRow">>, item: UnifiedInboxItem): string {
  if (!item.preview) return tUi("noPreview")
  return item.previewDirection === "outbound" ? tUi("youValue0", { value0: String(item.preview) }) : item.preview
}
